import type {DocsItem} from '@academy/components';

import {getPrimaryKidpreneursAcademyLink} from 'utils/getKidpreneursAcademyLink';
import {getPrimaryKidsCraftsLink} from 'utils/getKidsCraftsLink';

export const AGREEMENT_CLIENT_STATIC: DocsItem[] = [
    {
        title: "",
        content: [
          {
            html: `These User Agreement and Terms of Use (the “Terms”) govern your use of the websites and services operated by Ed Technologies Corp. (“Ed Technologies,” “we,” “us,” or “our”) and, where applicable, its affiliated companies and entities, including:`,
          },
          {
            html: `<ul class="list-inside list-disc mb-[1rem]">
              <li class="docs__item mb-[1rem]">KidEntrepreneurs.academy or Kidpreneurs.academy — educational classes and programs;</li>
              <li class="docs__item mb-[1rem]">KidsCrafts.shop — an educational marketplace where children can learn and practice business skills under the account, consent, and supervision of a parent or legal guardian;</li>
              <li class="docs__item mb-[1rem]">OnlineSafetyReadiness.com — online safety education, training, assessments, and recommendations; and</li>
              <li class="docs__item">AfterSchoolVote.com — a platform for parents and guardians to vote on educational courses and programs.</li>
            </ul>`,
          },
          {
            html: `Together, these are the “Services.” By accessing or using the Services, you agree to these Terms and our Privacy Policy. If you do not agree, please do not use the Services.`,
          },
        ],
      },
      {
        title: "1. Who Is Bound by These Terms",
        content: [
          {
            html: `Because minors generally lack the legal capacity to enter into a binding contract, a child or student (a “Child User”) may access the Services only under the account of, and with the consent and supervision of, a parent, legal guardian, school, school district, or other authorized organization (an “Authorized Adult”). On KidsCrafts.shop specifically, every seller account is created and owned by an Authorized Adult, as described in Section 3.1 — a Child User never holds an account or submits a listing in their own name.`,
          },
          {
            html: `If you are an Authorized Adult, you accept these Terms on your own behalf and on behalf of any Child User you authorize to use the Services under your account. You remain responsible for that Child User’s compliance with these Terms, including the sections on Payments, Acceptable Use, User Content, and Indemnification. Provisions of these Terms that impose payment obligations, indemnification obligations, or other binding legal commitments apply to the Authorized Adult, not to the Child User directly.`,
          },
          {
            html: `If Services are accessed through a school, district, or educational organization, that organization’s order form, data processing agreement, or other governing agreement with Ed Technologies applies to its use, and these Terms apply to the extent not otherwise addressed in that agreement.`,
          },
        ],
      },
      {
        title: "2. Eligibility and Accounts",
        content: [
          {
            list: {
              description: "",
              items: [
                "Authorized Adults must be at least 18 years old and have legal authority to consent on behalf of any Child User they authorize.",
                "Where a Child User accesses the Services, the required authorization and consent must be provided by a parent, legal guardian, school, school district, or other authorized organization, as applicable.",
                "For KidsCrafts.shop specifically, if a Child User is under 13, Ed Technologies obtains verifiable parental consent before collecting personal information from that child, arising from the account-creation process described in Section 3.1 and Section 5 of the Privacy Policy.",
                "Authorized Adults are responsible for supervising a Child User’s use of the Services and for all activity conducted through accounts they create or manage.",
                "You agree to provide accurate information, keep account credentials secure, and notify us promptly of any unauthorized access.",
                "Schools and organizations may manage, suspend, or terminate accounts under their administration.",
              ],
            },
          },
        ],
      },
      {
        title: "3. KidsCrafts.shop — Educational Marketplace",
        content: [
          {
            html: `KidsCrafts.shop is an educational marketplace designed to help children learn and practice business and sales skills in a monitored environment, operated entirely through accounts owned and controlled by an Authorized Adult.`,
          },
        ],
      },
      {
        title: "3.1 Account Creation and Ownership",
        content: [
          {
            html: `A KidsCrafts.shop seller account may be created only by the Authorized Adult who enrolled the child in an Ed Technologies program. During enrollment, the Authorized Adult provides their own email address and payment information to purchase the program; upon completing that purchase, we send an invitation to create a KidsCrafts.shop seller account to that same email address. The resulting seller account is created, owned, and controlled by the Authorized Adult, not by the Child User. A Child User does not hold a separate account or credentials on KidsCrafts.shop.`,
          },
        ],
      },
      {
        title: "3.2 Child Participation Under Supervision",
        content: [
          {
            html: `A Child User may help prepare a listing — for example, drafting a product description, choosing a price, or selecting images — as part of practicing business skills, but only while supervised by the Authorized Adult on that account. A listing may be submitted for review only by the Authorized Adult, using the final submission control on the account; a Child User cannot submit a listing directly. By submitting a listing, the Authorized Adult confirms that they have reviewed it, that it complies with Section 3.4 below, and that it is their own act for purposes of these Terms.`,
          },
        ],
      },
      {
        title: "3.3 Listing Content Requirements",
        content: [
          {
            html: `Listings must be appropriate for the platform’s intended audience and must not contain unnecessary personal information. Listings and any associated content may not include a Child User’s full name, a photograph that identifies the child together with an address or other locating detail, home address, phone number, personal email address, school name, or other information capable of identifying or locating the child, beyond what is reasonably needed to describe the product itself (e.g., product name, description, price, and non-identifying images of the item). Listings may not include illegal, dangerous, or prohibited goods or services.`,
          },
        ],
      },
      {
        title: "3.4 Moderation and Safety Screening",
        content: [
          {
            html: `In addition to review by the Authorized Adult at submission, every listing is screened by automated filters and, where warranted, by human moderators before it becomes visible to other users, in order to identify and remove personal information and content that violates these Terms. We may remove content, decline to publish a listing, or suspend an account that violates these Terms or creates a safety, legal, or security risk.`,
          },
        ],
      },
      {
        title: "3.5 Identity Verification and Purchasable Listings",
        content: [
          {
            html: `Before a listing can be made available for actual purchase by other users, the Authorized Adult’s account must complete identity verification through our payment processor (currently Stripe), which confirms that the account belongs to a verified adult. A listing that has not completed this verification cannot be purchased on the platform, regardless of whether it has otherwise been approved.`,
          },
        ],
      },
      {
        title: "3.6 Simulation Mode",
        content: [
          {
            html: `KidsCrafts.shop also offers a Simulation Mode in which identity verification through Stripe is not required. In Simulation Mode, a listing submitted by the Authorized Adult is reviewed and must be approved by our support team before it is published, but it cannot be purchased by other users under any circumstances. Simulation Mode exists so a Child User can practice preparing and publishing a listing without enabling a real transaction.`,
          },
        ],
      },
      {
        title: "3.7 Real Transactions, Product Safety, and Merchant Responsibility",
        content: [
          {
            html: `Where a purchasable listing (Section 3.5) results in an actual sale of a physical product, the Authorized Adult who owns the account is the seller and merchant of record for that transaction. Ed Technologies provides the platform that facilitates the listing and payment processing, but is not the manufacturer, seller, or shipper of the product and does not inspect products for compliance with consumer product safety requirements beyond the content moderation described in Section 3.4.`,
          },
          {
            html: `Authorized Adults are responsible for ensuring that any physical product listed or sold through KidsCrafts.shop complies with applicable consumer product safety laws (including, where applicable, the U.S. Consumer Product Safety Improvement Act and the Canada Consumer Product Safety Act), including any required age grading, choking-hazard warnings, and material safety considerations, particularly for products that may be purchased for or by young children. This section does not apply to listings created in Simulation Mode, which cannot be purchased.`,
          },
        ],
      },
      {
        title: "4. AfterSchoolVote.com",
        content: [
          {
            html: `AfterSchoolVote.com is intended for parents and guardians to vote on and express their preferences regarding educational courses or programs they would like offered at their children’s schools. We may reject duplicate, fraudulent, automated, or unauthorized votes and may use voting results or aggregated preferences to evaluate interest in potential educational programs. AfterSchoolVote.com is not intended for use by children; see Section 5 of the Privacy Policy.`,
          },
        ],
      },
      {
        title: "5. OnlineSafetyReadiness.com",
        content: [
          {
            html: `OnlineSafetyReadiness.com provides online safety education, training, assessments, reports, and recommendations. It may be used by students, parents, guardians, educators, schools, districts, and other authorized organizations. Assessments, reports, and recommendations, including any AI-generated recommendations, are intended to support — and not replace — the judgment of parents, educators, administrators, or qualified professionals. We do not guarantee that the Services will identify every safety risk, accurately predict behavior, or produce a particular educational or safety outcome.`,
          },
        ],
      },
      {
        title: "6. Acceptable Use",
        content: [
          {
            list: {
              description: "You agree not to:",
              items: [
                "Use the Services for unlawful, fraudulent, harmful, or unauthorized purposes;",
                "Access another user’s account or information without authorization;",
                "Submit false, misleading, illegal, or inappropriate content;",
                "Upload malware or interfere with the security or operation of the Services;",
                "Circumvent moderation or security measures;",
                "Copy, reverse engineer, modify, or exploit our technology without authorization; or",
                "Violate the rights, privacy, or safety of others.",
              ],
            },
          },
          {
            html: `We may monitor or review activity and content where appropriate and permitted by law, remove content, or suspend or terminate accounts when necessary to protect users or the Services.`,
          },
        ],
      },
      {
        title: "7. Payments",
        content: [
          {
            html: `Payments for classes, programs, products, or other Services may be processed through third-party payment providers. The Authorized Adult is responsible for all payments, including those relating to a Child User’s marketplace activity on KidsCrafts.shop. You agree to provide accurate payment information and authorize applicable charges. Nothing in these Terms limits mandatory consumer rights.`,
          },
        ],
      },
      {
        title: "7.1 Program Refunds and Cancellations",
        content: [
          {
            html: `Unless a specific program’s purchase page states otherwise, requests to withdraw from a KidEntrepreneurs.academy, Kidpreneurs.academy, or OnlineSafetyReadiness.com program are handled according to the following schedule:`,
          },
          {
            html: `<div class="overflow-x-auto mb-[1rem]">
              <table class="w-full border-collapse text-left text-[0.95em]">
                <thead>
                  <tr>
                    <th class="border border-black/20 bg-[#1A1A2E] text-white px-3 py-2 font-semibold">When Parent/Guardian Withdraws</th>
                    <th class="border border-black/20 bg-[#1A1A2E] text-white px-3 py-2 font-semibold">Refund</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="bg-[#F7F7FA]">
                    <td class="border border-black/20 px-3 py-2">14+ days before program starts</td>
                    <td class="border border-black/20 px-3 py-2">100% refund</td>
                  </tr>
                  <tr>
                    <td class="border border-black/20 px-3 py-2">7–13 days before start</td>
                    <td class="border border-black/20 px-3 py-2">75% refund</td>
                  </tr>
                  <tr class="bg-[#F7F7FA]">
                    <td class="border border-black/20 px-3 py-2">Less than 7 days before start</td>
                    <td class="border border-black/20 px-3 py-2">50% refund</td>
                  </tr>
                  <tr>
                    <td class="border border-black/20 px-3 py-2">After the program starts</td>
                    <td class="border border-black/20 px-3 py-2">No refund for sessions already provided; 50% of remaining unused sessions</td>
                  </tr>
                  <tr class="bg-[#F7F7FA]">
                    <td class="border border-black/20 px-3 py-2">Medical/family emergency</td>
                    <td class="border border-black/20 px-3 py-2">Case-by-case, with flexibility</td>
                  </tr>
                  <tr>
                    <td class="border border-black/20 px-3 py-2">Program cancelled by Ed Technologies Corp.</td>
                    <td class="border border-black/20 px-3 py-2">100% refund for affected sessions</td>
                  </tr>
                </tbody>
              </table>
            </div>`,
          },
          {
            html: `If we cancel a program, session, or class, Authorized Adults receive a 100% refund for the affected sessions. Refund requests relating to a medical or family emergency will be reviewed case-by-case, and we will apply reasonable flexibility beyond the schedule above where the circumstances warrant it. Nothing in this schedule limits mandatory consumer protection rights that cannot be waived under applicable law.`,
          },
          {
            html: `This refund schedule applies to program and class enrollments. It does not apply to purchases made through KidsCrafts.shop listings, which are transactions between the buyer and the Authorized Adult seller and are not governed by this section.`,
          },
        ],
      },
      {
        title: "8. User Content",
        content: [
          {
            html: `Some Services allow users to submit content, including product listings, images, descriptions, prices, comments, or other materials (“User Content”). The Authorized Adult remains responsible for User Content submitted by or on behalf of a Child User under their account and represents that they have the necessary rights and permissions for that content.`,
          },
          {
            html: `You grant Ed Technologies a non-exclusive, worldwide, royalty-free license to host, store, display, reproduce, and use User Content as reasonably necessary to operate, maintain, secure, and provide the Services. For User Content associated with a Child User, this license is limited to operating, securing, and providing the Services and does not include use of that content for external marketing or promotional purposes; any such use requires the separate consent described in the Photo and Video Consent section of the Privacy Policy. For KidsCrafts.shop, approved product listings may be visible to other users or the public, subject to Section 3.2 above.`,
          },
        ],
      },
      {
        title: "9. Intellectual Property",
        content: [
          {
            html: `The Services and their content, including educational materials, assessments, text, graphics, software, designs, logos, and trademarks, are owned by or licensed to Ed Technologies. You may use them only as permitted by these Terms or applicable law. You may not copy, distribute, modify, sell, or commercially exploit our proprietary content or technology without permission.`,
          },
        ],
      },
      {
        title: "10. Privacy, Student Data, and Parental Consent",
        content: [
          {
            html: `Our collection and use of personal information are described in our Privacy Policy, which forms part of these Terms. We do not ask an Authorized Adult to sign a separate consent form. Instead, verifiable parental consent for KidsCrafts.shop is built into the account-creation process described in Section 3.1: because the seller account can only be created through an invitation sent to the email address the Authorized Adult used to enroll and pay for a program, account creation itself — together with the direct notice we provide at that time — constitutes the Authorized Adult’s verifiable consent to the collection, use, and, where applicable, public display of information described in Section 3 and the Privacy Policy. Section 5 of the Privacy Policy describes this process, and the further identity verification that applies before a listing becomes purchasable, in more detail.`,
          },
          {
            html: `When Services are provided through schools or educational organizations, Ed Technologies may process student information on their behalf and in accordance with applicable agreements and law. Where applicable, Ed Technologies may act as a service provider or “school official” under FERPA and complies with applicable Canadian privacy laws. Parents, guardians, schools, and organizations are responsible for providing required authorizations or consents for a child’s or student’s use of the Services, except where Ed Technologies has agreed to obtain such consent directly, as it does for KidsCrafts.shop under Section 5 of the Privacy Policy.`,
          },
        ],
      },
      {
        title: "11. Third-Party Services",
        content: [
          {
            html: `The Services may use third-party providers for payments, hosting, analytics, communications, technology, AI, and other functions. Third-party services may have their own terms and privacy policies. Ed Technologies is not responsible for third-party practices.`,
          },
        ],
      },
      {
        title: "12. Availability and Termination",
        content: [
          {
            html: `We do not guarantee that the Services will always be available, uninterrupted, secure, or error-free. We may modify, suspend, or discontinue Services for technical, operational, security, legal, or business reasons. We may suspend or terminate access if we reasonably believe these Terms or applicable laws have been violated, or that suspension is necessary to protect users or the Services.`,
          },
          {
            html: `Upon termination, access may be revoked and applicable content or account information may be deleted, subject to our Privacy Policy (including the retention periods described there), applicable agreements, and legal requirements.`,
          },
        ],
      },
      {
        title: "13. Disclaimers and Limitation of Liability",
        content: [
          {
            html: `To the fullest extent permitted by law, the Services are provided on an “as is” and “as available” basis. Ed Technologies does not guarantee the accuracy, completeness, availability, or suitability of the Services or any AI-generated information.`,
          },
          {
            html: `With respect to KidsCrafts.shop, Ed Technologies does not manufacture, inspect for safety compliance, or guarantee the safety, quality, legality, age-appropriateness, or fitness for a particular purpose of any product listed or sold through the marketplace. Buyers and sellers transact at their own risk, and the responsibilities described in Section 3.4 apply.`,
          },
          {
            html: `To the fullest extent permitted by law, Ed Technologies Corp., its affiliates, officers, employees, contractors, and partners will not be liable for indirect, incidental, special, consequential, or punitive damages arising from your use of the Services. Nothing in these Terms excludes or limits liability that cannot legally be excluded or limited, including liability for death or personal injury caused by negligence, or liability that cannot be excluded under applicable consumer protection law.`,
          },
        ],
      },
      {
        title: "14. Indemnification",
        content: [
          {
            html: `To the fullest extent permitted by law, the Authorized Adult agrees to indemnify and hold harmless Ed Technologies Corp. and its affiliates, officers, employees, contractors, and partners from claims, damages, losses, liabilities, and reasonable expenses arising from (a) the Authorized Adult’s violation of these Terms, (b) unlawful use of the Services by the Authorized Adult or a Child User under their account, (c) User Content submitted under their account, or (d) violation of applicable law or third-party rights by the Authorized Adult or a Child User under their account. This obligation applies to the Authorized Adult and not to a Child User directly, consistent with Section 1.`,
          },
        ],
      },
      {
        title: "15. Informal Resolution and Governing Law",
        content: [
          {
            html: `If a dispute arises, please contact us first at the address below so we can try to resolve it informally. These Terms are governed by the laws of the Province of British Columbia and the applicable laws of Canada, without regard to conflict-of-law principles.`,
          },
          {
            html: `For users outside British Columbia or Canada, applicable mandatory consumer protection, privacy (including COPPA for U.S. users), education, and child protection laws may also apply regardless of this governing law clause. Nothing in these Terms is intended to waive rights or protections that cannot legally be waived, including any limits under applicable law on mandatory arbitration of claims involving minors.`,
          },
        ],
      },
      {
        title: "16. General Provisions",
        content: [
          {
            html: `<ul class="list-inside list-disc mb-[1rem]">
              <li class="docs__item mb-[1rem]"><strong>Severability:</strong> If any provision of these Terms is found unenforceable, the remaining provisions remain in full effect.</li>
              <li class="docs__item mb-[1rem]"><strong>Entire Agreement:</strong> These Terms, together with the Privacy Policy and any applicable order form or data processing agreement, constitute the entire agreement between you and Ed Technologies regarding the Services.</li>
              <li class="docs__item mb-[1rem]"><strong>No Waiver:</strong> Our failure to enforce a provision is not a waiver of our right to do so later.</li>
              <li class="docs__item mb-[1rem]"><strong>Assignment:</strong> You may not assign these Terms without our consent; we may assign these Terms in connection with a merger, acquisition, or sale of assets.</li>
              <li class="docs__item"><strong>Notices:</strong> We may provide notices to you via the email address on your account or by posting on the applicable Service.</li>
            </ul>`,
          },
        ],
      },
      {
        title: "17. Changes to These Terms",
        content: [
          {
            html: `We may update these Terms from time to time. Updated Terms will be posted on the applicable websites. Where required by law, we will provide additional notice of material changes, including to Authorized Adults who have provided consent on behalf of a Child User where the change affects the scope of that consent.`,
          },
        ],
      },
      {
        title: "18. Contact Us",
        content: [
          {
            html: `If you have questions about these Terms or our Services, please contact us:<br><br>
            Attn: Ed Technologies Corp.<br><br>
            Email: <a class="underline text-primary" href="mailto:service@KidEntrepreneurs.academy">service@KidEntrepreneurs.academy</a>`,
          },
        ],
      },
];
